import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, SpacingMode } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';

export class HexagonalLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 2.0;
    public readonly defaultOffsetZ: number = 0;
    
    constructor() {
        super(
            'hexagonal',
            'Arranges objects in a hexagonal honeycomb pattern on X-Y plane with configurable spacing',
            {
                hexSpacing: 2.0,
                hexOffsetZ: 0
            },
            '2d', // Hexagonal is 2D
            [SpacingMode.Fixed, SpacingMode.Distribute] // Support both spacing modes
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const userSpacing = this.getParameter(layoutData, 'hexSpacing', this.defaultSpacing);
        const offsetZ = this.getParameter(layoutData, 'hexOffsetZ', this.defaultOffsetZ);
        
        // Center hexagon
        if (index === 0) {
            return new Vector3(0, 0, offsetZ);
        }
        
        // Convert index to axial coordinates (q, r) for proper hexagonal grid
        const { q, r } = this.indexToAxialCoordinates(index);
        
        // Calculate perfect hexagon spacing for touching hexagons
        // For hexagons to touch perfectly, we need to account for the actual hexagon geometry
        // The hexagon scale doesn't directly correspond to the spacing needed
        const hexagonScale = this.getUniformScale();
        
        // Empirical calibration for perfect hexagon tiling:
        // Let's test different values to find the perfect fit
        // Starting with smaller values since Math.sqrt(3) ≈ 1.73 was too large
        const effectiveCircumradius = 0.5 * hexagonScale;
        
        // Test values (uncomment one at a time to calibrate):
        // const perfectSpacing = effectiveCircumradius * 1.5;  // Too big
        // const perfectSpacing = effectiveCircumradius * 1.3;  // Better but still too big
        const perfectSpacing = effectiveCircumradius * 1.1;  // Even tighter!
        // const perfectSpacing = effectiveCircumradius * 1.0;  // Same as circumradius
        // const perfectSpacing = effectiveCircumradius * 0.87; // Original empirical value
        
        // Add user spacing as additional gap
        const totalSpacing = perfectSpacing + userSpacing;
        
        // Convert axial coordinates to cartesian coordinates using correct hex grid math
        // In a proper hexagonal grid:
        // x = totalSpacing * (3/2 * q)
        // y = totalSpacing * (sqrt(3)/2 * q + sqrt(3) * r)
        const x = totalSpacing * (3/2 * q);
        const y = totalSpacing * (Math.sqrt(3)/2 * q + Math.sqrt(3) * r);
        
        return new Vector3(x, y, offsetZ);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const position = this.calculatePosition(index, totalCount, layoutData);
        
        // Use the new quaternion-based orientation calculation
        return this.orientationToEulerAngles(
            position, index, totalCount, layoutData
        );
    }
    
    calculateScale(index: number, totalCount: number, layoutData: LayoutData): number {
        // Return uniform scale for perfect hexagonal tiling
        return this.getUniformScale();
    }
    
    /**
     * Direct application of layout to POIs - much cleaner than calculate* methods!
     * Sets position, rotation AND scale for perfect hexagonal tiling.
     */
    applyToObjects(pois: any[], layoutData: LayoutData): void {
        const uniformScale = this.getUniformScale();
        
        pois.forEach((poi, index) => {
            // Calculate position using existing logic
            const position = this.calculatePosition(index, pois.length, layoutData);
            const orientation = this.calculateOrientation(index, pois.length, layoutData);
            
            // Apply all transformations directly to POI data
            poi.poidata.position.value = position;
            poi.poidata.rotation.value = orientation;
            poi.poidata.scale.value = uniformScale;
            
            ////console.log(`🐝 Applied hexagonal layout to POI ${poi.name}: pos(${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)}) rot(${orientation.x.toFixed(1)}°, ${orientation.y.toFixed(1)}°, ${orientation.z.toFixed(1)}°) scale(${uniformScale.toFixed(2)})`);
        });
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // CONSISTENT FIRST ORIENTATION: Index 0 has no rotation
        if (index === 0) {
            return Quaternion.identity();
        }
        
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        // Calculate surface frame for hexagonal plane (2D surface)
        const surfaceFrame = this.calculateHexSurfaceFrame();
        
        // Determine forward and up directions using alignment system
        const desiredForward = this.determineForwardDirection(alignmentMode, undefined, surfaceFrame);
        const desiredUp = this.determineUpDirection(upVectorMode, undefined, surfaceFrame);
        
        // Build orthogonal coordinate system
        const { finalRight, finalUp, finalForward } = this.buildOrthogonalSystem(
            desiredForward, desiredUp, upVectorMode
        );
        
        // Create quaternion from orthogonal system
        return Quaternion.lookDir(finalForward, finalUp);
    }
    
    /**
     * Calculate surface frame for hexagonal plane (2D surface)
     * Hexagonal grid is a planar surface in XY-plane with normal pointing up in Z
     */
    private calculateHexSurfaceFrame(): {
        uTangent: Vector3,
        vTangent: Vector3,
        normal: Vector3
    } {
        // For hexagonal plane (XY-plane):
        // U-tangent points along X-axis
        const uTangent = new Vector3(1, 0, 0);
        
        // V-tangent points along Y-axis
        const vTangent = new Vector3(0, 1, 0);
        
        // Surface normal points up along Z-axis
        const normal = new Vector3(0, 0, 1);
        
        return { uTangent, vTangent, normal };
    }
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'hexFixedSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        const width = this.getParameter(layoutData, 'hexWidth', 10.0);
        const height = this.getParameter(layoutData, 'hexHeight', 10.0);
        
        // Calculate spacing based on hexagonal grid dimensions and object count
        // This is an approximation for uniform distribution in a bounded area
        const area = width * height;
        const areaPerObject = area / objectCount;
        
        // For hexagonal tiling, approximate spacing
        return Math.sqrt(areaPerObject / (3 * Math.sqrt(3) / 2));
    }
    
    /**
     * Convert linear index to axial coordinates (q, r) for proper hexagonal grid
     * This creates the correct honeycomb pattern with proper neighbor relationships
     */
    private indexToAxialCoordinates(index: number): { q: number; r: number } {
        if (index === 0) {
            return { q: 0, r: 0 }; // Center
        }
        
        // Predefined coordinates for perfect hexagonal spiral pattern
        // Ring 1: The 6 direct neighbors of center
        const ring1 = [
            { q: 1, r: 0 },   // East
            { q: 0, r: 1 },   // Southeast  
            { q: -1, r: 1 },  // Southwest
            { q: -1, r: 0 },  // West
            { q: 0, r: -1 },  // Northwest
            { q: 1, r: -1 }   // Northeast
        ];
        
        if (index <= 6) {
            return ring1[index - 1];
        }
        
        // For higher rings, calculate systematically
        let currentIndex = 7; // Start after ring 1
        let ring = 2;
        
        while (true) {
            const hexagonsInRing = ring * 6;
            
            if (index < currentIndex + hexagonsInRing) {
                // Found the ring! Now calculate position within ring
                const positionInRing = index - currentIndex;
                return this.calculateAxialForRing(ring, positionInRing);
            }
            
            currentIndex += hexagonsInRing;
            ring++;
        }
    }
    
    /**
     * Calculate axial coordinates for a given ring and position within that ring
     */
    private calculateAxialForRing(ring: number, positionInRing: number): { q: number; r: number } {
        // For ring 2 and beyond, we walk around the ring in 6 segments
        const segmentLength = ring;
        const segment = Math.floor(positionInRing / segmentLength);
        const positionInSegment = positionInRing % segmentLength;
        
        // Starting positions for each segment (corners of the ring)
        const ringCorners = [
            { q: ring, r: -ring },      // Top-right corner
            { q: 0, r: 0 },             // Will be calculated
            { q: -ring, r: ring },      // Bottom corner  
            { q: -ring, r: ring },      // Bottom-left corner
            { q: 0, r: 0 },             // Will be calculated
            { q: ring, r: -ring }       // Top corner
        ];
        
        // Direction vectors for each segment
        const directions = [
            { q: -1, r: 1 },  // Southeast direction
            { q: -1, r: 0 },  // West direction
            { q: 0, r: -1 },  // Northwest direction  
            { q: 1, r: -1 },  // Northeast direction
            { q: 1, r: 0 },   // East direction
            { q: 0, r: 1 }    // Southeast direction (back to start)
        ];
        
        // Calculate starting position for this segment
        let startQ: number, startR: number;
        
        switch (segment) {
            case 0: // Right edge, going down-left
                startQ = ring;
                startR = -ring + positionInSegment;
                break;
            case 1: // Down-right edge
                startQ = ring - positionInSegment;
                startR = positionInSegment;
                break;
            case 2: // Bottom edge, going left
                startQ = -positionInSegment;
                startR = ring;
                break;
            case 3: // Left edge, going up-right
                startQ = -ring;
                startR = ring - positionInSegment;
                break;
            case 4: // Up-left edge
                startQ = -ring + positionInSegment;
                startR = -positionInSegment;
                break;
            case 5: // Top edge, going right
                startQ = positionInSegment;
                startR = -ring;
                break;
            default:
                startQ = 0;
                startR = 0;
        }
        
        return { q: startQ, r: startR };
    }
    
    /**
     * Get uniform scale for all hexagons in the layout
     * This ensures all hexagons have the same size for perfect tiling
     */
    private getUniformScale(): number {
        // For now, use a standard scale of 0.8
        // TODO: In the future, this could read the actual POI scales and enforce uniformity
        return 0.8;
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'hexSpacing', this.defaultSpacing);
        return super.validateParameters(layoutData) && spacing >= 0; // Allow 0 spacing for perfect fit
    }
    
    /**
     * Calculate how many hexagons fit in a given number of rings
     */
    getHexagonCount(rings: number): number {
        if (rings <= 0) return 1; // Just center
        return 1 + 3 * rings * (rings + 1); // Center + rings
    }
    
    /**
     * Calculate which ring an index belongs to
     */
    getRingForIndex(index: number): number {
        if (index === 0) return 0;
        
        let ring = 1;
        let totalHexes = 1;
        
        while (totalHexes <= index) {
            totalHexes += ring * 6;
            if (totalHexes > index) break;
            ring++;
        }
        
        return ring;
    }
    
    // Clone method
    clone(): HexagonalLayouter {
        return new HexagonalLayouter();
    }
    
    // Factory method
    static create(): HexagonalLayouter {
        return new HexagonalLayouter();
    }
}

export class SphericalHexagonalLayouter extends BaseLayouter {
    public readonly defaultRadius: number = 8.0;
    public readonly defaultSpacing: number = 2.0;
    public readonly defaultSubdivisions: number = 2;
    
    constructor() {
        super(
            'spherical_hexagonal',
            'Distributes objects on a spherical surface in a hexagonal pattern with controllable spacing',
            {
                sphereRadius: 8.0,
                hexSpacing: 2.0,
                sphereSubdivisions: 2 // Controls density
            },
            '2d', // Spherical hexagonal is 2D surface
            [SpacingMode.Fixed, SpacingMode.Distribute] // Support both spacing modes
        );
    }
    
        calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const radius = this.getParameter(layoutData, 'sphereRadius', this.defaultRadius);
        const hexSpacing = this.getParameter(layoutData, 'hexSpacing', this.defaultSpacing);
        
        // Use the same ring system as 2D hexagonal, but project onto sphere
        let x: number, y: number;
        
        if (index === 0) {
            // Center hexagon at front of sphere
            x = 0;
            y = 0;
        } else {
            // Use same hexagonal grid math as 2D version
            const { q, r } = this.indexToAxialCoordinates(index);
            
            // Calculate perfect spacing for touching hexagons on sphere
            const hexagonScale = this.getUniformScale();
            
            // Empirical calibration for perfect hexagon tiling on sphere:
            // Using same calibration as 2D version
            const effectiveCircumradius = 0.5 * hexagonScale;
            const perfectSpacing = effectiveCircumradius * 1.25 * (radius / 10.0);
            
            // Add user spacing as additional gap
            const totalSpacing = perfectSpacing + (hexSpacing * (radius / 10.0));
            
            x = totalSpacing * (3/2 * q);
            y = totalSpacing * (Math.sqrt(3)/2 * q + Math.sqrt(3) * r);
        }
        
        // Project flat hexagonal pattern onto sphere surface
        // Start with normalized coordinates
        const u = x / radius;
        const v = y / radius;
        
        // Calculate z coordinate to put point on sphere surface
        const uv_squared = u * u + v * v;
        let z: number;
        
        if (uv_squared <= 1.0) {
            // Point is within unit circle, calculate z to be on sphere
            z = Math.sqrt(Math.max(0, 1 - uv_squared));
        } else {
            // Point is outside unit circle, project it back
            const scale = 1.0 / Math.sqrt(uv_squared);
            const scaled_u = u * scale;
            const scaled_v = v * scale;
            z = 0; // On the equator
            
            return new Vector3(scaled_u * radius, scaled_v * radius, z * radius);
        }
        
        return new Vector3(u * radius, v * radius, z * radius);
    }
    
    /**
     * Convert linear index to axial coordinates (q, r) for proper hexagonal grid
     * Same logic as HexagonalLayouter
     */
    private indexToAxialCoordinates(index: number): { q: number; r: number } {
        if (index === 0) {
            return { q: 0, r: 0 }; // Center
        }
        
        // Predefined coordinates for perfect hexagonal spiral pattern
        // Ring 1: The 6 direct neighbors of center
        const ring1 = [
            { q: 1, r: 0 },   // East
            { q: 0, r: 1 },   // Southeast  
            { q: -1, r: 1 },  // Southwest
            { q: -1, r: 0 },  // West
            { q: 0, r: -1 },  // Northwest
            { q: 1, r: -1 }   // Northeast
        ];
        
        if (index <= 6) {
            return ring1[index - 1];
        }
        
        // For higher rings, calculate systematically
        let currentIndex = 7; // Start after ring 1
        let ring = 2;
        
        while (true) {
            const hexagonsInRing = ring * 6;
            
            if (index < currentIndex + hexagonsInRing) {
                // Found the ring! Now calculate position within ring
                const positionInRing = index - currentIndex;
                return this.calculateAxialForRing(ring, positionInRing);
            }
            
            currentIndex += hexagonsInRing;
            ring++;
        }
    }
    
    /**
     * Calculate axial coordinates for a given ring and position within that ring
     * Same logic as HexagonalLayouter
     */
    private calculateAxialForRing(ring: number, positionInRing: number): { q: number; r: number } {
        // For ring 2 and beyond, we walk around the ring in 6 segments
        const segmentLength = ring;
        const segment = Math.floor(positionInRing / segmentLength);
        const positionInSegment = positionInRing % segmentLength;
        
        // Calculate starting position for this segment
        let startQ: number, startR: number;
        
        switch (segment) {
            case 0: // Right edge, going down-left
                startQ = ring;
                startR = -ring + positionInSegment;
                break;
            case 1: // Down-right edge
                startQ = ring - positionInSegment;
                startR = positionInSegment;
                break;
            case 2: // Bottom edge, going left
                startQ = -positionInSegment;
                startR = ring;
                break;
            case 3: // Left edge, going up-right
                startQ = -ring;
                startR = ring - positionInSegment;
                break;
            case 4: // Up-left edge
                startQ = -ring + positionInSegment;
                startR = -positionInSegment;
                break;
            case 5: // Top edge, going right
                startQ = positionInSegment;
                startR = -ring;
                break;
            default:
                startQ = 0;
                startR = 0;
        }
        
        return { q: startQ, r: startR };
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const position = this.calculatePosition(index, totalCount, layoutData);
        
        // Use the new quaternion-based orientation calculation
        return this.orientationToEulerAngles(
            position, index, totalCount, layoutData
        );
    }
    
    calculateScale(index: number, totalCount: number, layoutData: LayoutData): number {
        // Return uniform scale for perfect hexagonal tiling
        return this.getUniformScale();
    }
    
    /**
     * Direct application of layout to POIs - much cleaner than calculate* methods!
     * Sets position, rotation AND scale for perfect spherical hexagonal tiling.
     */
    applyToObjects(pois: any[], layoutData: LayoutData): void {
        const uniformScale = this.getUniformScale();
        
        pois.forEach((poi, index) => {
            // Calculate position using existing logic
            const position = this.calculatePosition(index, pois.length, layoutData);
            const orientation = this.calculateOrientation(index, pois.length, layoutData);
            
            // Apply all transformations directly to POI data
            poi.poidata.position.value = position;
            poi.poidata.rotation.value = orientation;
            poi.poidata.scale.value = uniformScale;
            
            ////console.log(`🌍 Applied spherical hexagonal layout to POI ${poi.name}: pos(${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)}) rot(${orientation.x.toFixed(1)}°, ${orientation.y.toFixed(1)}°, ${orientation.z.toFixed(1)}°) scale(${uniformScale.toFixed(2)})`);
        });
    }
    

    
    /**
     * Get uniform scale for all hexagons in the layout
     * This ensures all hexagons have the same size for perfect tiling
     */
    private getUniformScale(): number {
        // For now, use a standard scale of 0.8
        // TODO: In the future, this could read the actual POI scales and enforce uniformity
        return 0.8;
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'sphereRadius', this.defaultRadius);
        const spacing = this.getParameter(layoutData, 'hexSpacing', this.defaultSpacing);
        return super.validateParameters(layoutData) && radius > 0 && spacing >= 0; // Allow 0 spacing for perfect fit
    }
    
    /**
     * Calculate optimal number of objects for good sphere coverage
     */
    getOptimalObjectCount(radius: number): number {
        // Rough approximation: more objects for larger spheres
        return Math.max(12, Math.floor(radius * radius * 0.8));
    }
    
    /**
     * Calculate surface area coverage per object
     */
    getCoveragePerObject(radius: number, objectCount: number): number {
        const sphereSurfaceArea = 4 * Math.PI * radius * radius;
        return sphereSurfaceArea / objectCount;
    }
    
    // Clone method
    clone(): SphericalHexagonalLayouter {
        return new SphericalHexagonalLayouter();
    }
    
    // Factory method
    static create(): SphericalHexagonalLayouter {
        return new SphericalHexagonalLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // CONSISTENT FIRST ORIENTATION: Index 0 has no rotation
        if (index === 0) {
            return Quaternion.identity();
        }
        
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        const radius = this.getParameter(layoutData, 'sphereRadius', this.defaultRadius);
        
        // Calculate surface frame for sphere (2D surface)
        const surfaceFrame = this.calculateSphereSurfaceFrame(position, radius);
        
        // Determine forward and up directions using alignment system
        const desiredForward = this.determineForwardDirection(alignmentMode, undefined, surfaceFrame);
        const desiredUp = this.determineUpDirection(upVectorMode, undefined, surfaceFrame);
        
        // Build orthogonal coordinate system
        const { finalRight, finalUp, finalForward } = this.buildOrthogonalSystem(
            desiredForward, desiredUp, upVectorMode
        );
        
        // Create quaternion from orthogonal system
        return Quaternion.lookDir(finalForward, finalUp);
    }
    
    /**
     * Calculate surface frame for sphere (2D surface)
     */
    private calculateSphereSurfaceFrame(position: Vector3, radius: number): {
        uTangent: Vector3,
        vTangent: Vector3,
        normal: Vector3
    } {
        // For sphere: normal points outward from center
        const normal = position.clone().normalize();
        
        // U-tangent: longitude direction (perpendicular to normal and world up)
        const worldUp = new Vector3(0, 1, 0);
        let uTangent = new Vector3().crossVectors(worldUp, normal);
        
        // Handle poles (when normal is parallel to world up)
        if (uTangent.length() < 0.001) {
            uTangent = new Vector3(1, 0, 0); // Fallback at poles
        }
        uTangent.normalize();
        
        // V-tangent: latitude direction (normal × u-tangent)
        const vTangent = new Vector3().crossVectors(normal, uTangent).normalize();
        
        return { uTangent, vTangent, normal };
    }
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'sphereFixedSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        const radius = this.getParameter(layoutData, 'sphereRadius', this.defaultRadius);
        
        // For sphere, calculate spacing based on surface area and object count
        const sphereSurfaceArea = 4 * Math.PI * radius * radius;
        const areaPerObject = sphereSurfaceArea / objectCount;
        
        // Approximate spacing as square root of area per object
        return Math.sqrt(areaPerObject);
    }
} 